import { useEffect, useMemo, useState } from "react";
import { TaskDisclosure } from "../components/TaskDisclosure";

const OPS_SECTION_IDS = ["board", "shooters", "safety", "equipment", "closeout"];

function normalizeOpsSection(value) {
  return OPS_SECTION_IDS.includes(value) ? value : "board";
}

function formatDateTime(value) {
  if (!value) {
    return "Not set";
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function dateOnly(value) {
  if (!value) {
    return "Not scheduled";
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

function formatTime(value) {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function toDateInput(date) {
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 10);
}

function toDateTimeInput(date = new Date()) {
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 16);
}

function dateTimeInputFromValue(value) {
  if (!value) {
    return toDateTimeInput(new Date());
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? toDateTimeInput(new Date()) : toDateTimeInput(date);
}

function memberLabel(member) {
  return member?.fullName || member?.email || (member ? "Member record" : "Member");
}

function membershipPlanLabel(member, membershipTypes = []) {
  if (!member?.membershipTypeId) {
    return "No plan";
  }
  const type = membershipTypes.find((item) => item.membershipTypeId === member.membershipTypeId);
  return type?.name || "Plan needs review";
}

function statusLabel(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function resourceTypeRank(resource) {
  const type = resource?.type || "";
  if (type === "area") {
    return 1;
  }
  if (type === "bay") {
    return 2;
  }
  if (type === "lane") {
    return 3;
  }
  return 4;
}

function resourceControlLabel(resource, includedCount = 0) {
  const type = resource?.type || "";
  if (type === "area") {
    return "Range section";
  }
  if (type === "bay") {
    return includedCount > 0 ? "Bay zone" : "Bay";
  }
  if (type === "lane") {
    return "Firing position";
  }
  return statusLabel(type || "Resource");
}

function memberEligibility(member) {
  const review = member?.reviewSummary || {};
  const flags = [];
  if (!member) {
    return ["No linked member"];
  }
  if (member.status !== "active") {
    flags.push(`Status: ${statusLabel(member.status || "unknown")}`);
  }
  if (review.waiverStatus && review.waiverStatus !== "completed") {
    flags.push(`Waiver: ${statusLabel(review.waiverStatus)}`);
  }
  if (review.prequalificationStatus && !["not_required", "completed", "approved"].includes(review.prequalificationStatus)) {
    flags.push(`Qualification: ${statusLabel(review.prequalificationStatus)}`);
  }
  return flags.length > 0 ? flags : ["Eligible"];
}

function reservationMember(reservation, members) {
  if (reservation.memberId) {
    return members.find((member) => member.memberId === reservation.memberId) || reservation.memberSnapshot || null;
  }
  const email = reservation.contactEmail || reservation.customerEmail || reservation.memberSnapshot?.email;
  return email
    ? members.find((member) => member.email === email) || reservation.memberSnapshot || null
    : reservation.memberSnapshot || null;
}

function reservationResourceName(reservation, resourceById) {
  return resourceById.get(reservation.resourceId)?.name ||
    reservation.resourceSnapshot?.name ||
    reservation.resourceName ||
    "Range resource";
}

function overlapsDate(item, dateKey) {
  if (!item?.startsAt || !item?.endsAt) {
    return false;
  }
  const starts = new Date(`${dateKey}T00:00:00`);
  const ends = new Date(starts);
  ends.setDate(ends.getDate() + 1);
  return Date.parse(item.startsAt) < ends.getTime() && starts.getTime() < Date.parse(item.endsAt);
}

function formatCycle(resource) {
  const cycle = resource.targetControl?.cycle || {};
  if (!cycle.enabled) {
    return "No cease-fire cycle";
  }
  return `${cycle.firingMinutes || 0} min hot / ${cycle.targetCheckMinutes || 0} min cease-fire`;
}

function cycleState(resource, now = new Date()) {
  const cycle = resource.targetControl?.cycle || {};
  if (!cycle.enabled) {
    return { label: "No cycle", detail: "Set a cease-fire cycle in resource detail.", tone: "muted" };
  }
  const firingMinutes = Number(cycle.firingMinutes || 0);
  const checkMinutes = Number(cycle.targetCheckMinutes || 0);
  const totalMinutes = firingMinutes + checkMinutes;
  if (totalMinutes <= 0) {
    return { label: "Cycle incomplete", detail: "Set hot and cease-fire minutes.", tone: "warning" };
  }
  const startsAt = cycle.startsAt ? new Date(cycle.startsAt) : new Date(now);
  if (Number.isNaN(startsAt.getTime())) {
    return { label: "Cycle needs start", detail: "Set a valid cycle start.", tone: "warning" };
  }
  if (now.getTime() < startsAt.getTime()) {
    const minutesUntil = Math.ceil((startsAt.getTime() - now.getTime()) / 60000);
    return { label: "Cycle pending", detail: `Starts in ${minutesUntil} min`, tone: "muted" };
  }
  const elapsedMinutes = Math.floor((now.getTime() - startsAt.getTime()) / 60000);
  const position = elapsedMinutes % totalMinutes;
  if (position < firingMinutes) {
    const minutesUntil = firingMinutes - position;
    return { label: "Live fire active", detail: `Scheduled cease-fire in ${minutesUntil} min`, tone: "active" };
  }
  const minutesUntil = totalMinutes - position;
  return { label: "Cease fire", detail: `Resume in ${minutesUntil} min`, tone: "warning" };
}

function resourceStatusTone(resource) {
  switch (resource?.status) {
    case "open":
      return "open";
    case "maintenance":
      return "maintenance";
    case "temporarily_reserved":
      return "reserved";
    case "closed":
      return "closed";
    default:
      return "muted";
  }
}

function aggregateTargetControlState(resources = [], mixedLabel = "Mixed line state") {
  const firingResources = resources.filter(Boolean);
  if (firingResources.length === 0) {
    return { label: "No firing controls", tone: "muted" };
  }
  const targetCheckCount = firingResources.filter((resource) => resource.targetControl?.status === "target_check").length;
  if (targetCheckCount === 0) {
    return { label: "Live fire active", tone: "active" };
  }
  if (targetCheckCount === firingResources.length) {
    return { label: "Cease fire", tone: "warning" };
  }
  return { label: mixedLabel, tone: "warning" };
}

function searchTokens(value) {
  const raw = String(value || "").toLowerCase();
  return {
    compact: raw.replace(/[^a-z0-9]+/g, ""),
    spaced: raw.replace(/[^a-z0-9]+/g, " ").trim()
  };
}

export function DailyOpsPage({
  loading,
  members = [],
  membershipTypes = [],
  onAddMemberFlag,
  onCreateOperationNote,
  onCreateReservation,
  onUpdateReservation,
  onUpdateResource,
  onUpdateResourceSectionCommand,
  operationNotes = [],
  operationNotesLoading = false,
  resourceBlocks = [],
  resources = [],
  reservations = [],
  savingOperationNote = false,
  updatingReservationId,
  updatingResourceId
}) {
  const [activeOpsSection, setActiveOpsSection] = useState(() => normalizeOpsSection(window.location.hash.replace("#operations-", "")));
  const [boardQuery, setBoardQuery] = useState("");
  const [boardResourceFilter, setBoardResourceFilter] = useState("");
  const [memberQuery, setMemberQuery] = useState("");
  const [rosterResourceFilter, setRosterResourceFilter] = useState("");
  const [selectedMemberId, setSelectedMemberId] = useState("");
  const [assignmentForm, setAssignmentForm] = useState({
    resourceId: "",
    startsAt: "",
    durationMinutes: "60",
    notes: ""
  });
  const [equipmentAssignments, setEquipmentAssignments] = useState({});
  const [equipmentMemberAssignments, setEquipmentMemberAssignments] = useState({});
  const [safetyActionId, setSafetyActionId] = useState("");
  const [safetyForm, setSafetyForm] = useState({
    memberId: "",
    type: "incident",
    severity: "warning",
    title: "",
    notes: ""
  });
  const [opsLog, setOpsLog] = useState([]);
  const [actionForm, setActionForm] = useState({
    action: "shift_note",
    resourceId: "",
    memberId: "",
    note: ""
  });
  const [cycleResourceId, setCycleResourceId] = useState("");
  const [cyclePickerQuery, setCyclePickerQuery] = useState("");
  const [cyclePickerOpen, setCyclePickerOpen] = useState(false);
  const [cycleDraft, setCycleDraft] = useState({
    enabled: true,
    firingMinutes: "50",
    targetCheckMinutes: "10",
    startsAt: toDateTimeInput(new Date()),
    note: "Scheduled target-check rhythm"
  });
  const [cycleNotice, setCycleNotice] = useState("");
  const [commandingResourceId, setCommandingResourceId] = useState("");
  const [expandedLineGroups, setExpandedLineGroups] = useState(() => new Set());
  const visibleOpsLog = useMemo(() => {
    const durableNotes = (operationNotes || []).map((note) => ({
      id: note.noteId || `${note.createdAt}-${note.action}`,
      createdAt: note.createdAt,
      memberName: note.memberName || "Range shift",
      action: note.action || "shift_note",
      resourceName: note.resourceName || "",
      note: note.note || "",
      durable: true
    }));
    const localOnly = opsLog
      .filter((item) => !durableNotes.some((note) => note.id === item.id))
      .map((item) => ({ ...item, durable: false }));
    return [...durableNotes, ...localOnly]
      .sort((left, right) => Date.parse(right.createdAt || 0) - Date.parse(left.createdAt || 0))
      .slice(0, 24);
  }, [operationNotes, opsLog]);

  useEffect(() => {
    const syncOpsSectionFromHash = () => {
      setActiveOpsSection(normalizeOpsSection(window.location.hash.replace("#operations-", "")));
    };
    window.addEventListener("hashchange", syncOpsSectionFromHash);
    return () => window.removeEventListener("hashchange", syncOpsSectionFromHash);
  }, []);

  const operatingDate = toDateInput(new Date());
  const operatingDateLabel = new Date(`${operatingDate}T12:00:00`).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric"
  });
  const resourceById = useMemo(() => new Map(resources.map((resource) => [resource.resourceId, resource])), [resources]);
  const childResourcesByParentId = useMemo(() => resources.reduce((accumulator, resource) => {
    if (!resource.parentResourceId) {
      return accumulator;
    }
    const children = accumulator.get(resource.parentResourceId) || [];
    children.push(resource);
    accumulator.set(resource.parentResourceId, children);
    return accumulator;
  }, new Map()), [resources]);
  const todaysReservations = useMemo(() => (
    reservations
      .filter((reservation) => overlapsDate(reservation, operatingDate))
      .sort((left, right) => String(left.startsAt || "").localeCompare(String(right.startsAt || "")))
  ), [operatingDate, reservations]);
  const dayReservations = useMemo(() => (
    todaysReservations
      .filter((reservation) => !rosterResourceFilter || reservation.resourceId === rosterResourceFilter)
  ), [rosterResourceFilter, todaysReservations]);
  const activeReservations = dayReservations.filter((reservation) => ["confirmed", "standby", "payment_pending", "checkout_started"].includes(reservation.status));
  const activeReservationsToday = todaysReservations.filter((reservation) => ["confirmed", "standby", "payment_pending", "checkout_started"].includes(reservation.status));
  const activeBlocks = useMemo(() => (
    resourceBlocks
      .filter((block) => block.status === "active" && overlapsDate(block, operatingDate))
      .sort((left, right) => String(left.startsAt || "").localeCompare(String(right.startsAt || "")))
  ), [operatingDate, resourceBlocks]);
  const resourceHasFiringControl = useMemo(() => {
    const hasFiringControl = (resource) => {
      if (["bay", "lane"].includes(resource?.type || "")) {
        return true;
      }
      const descendants = [];
      const collect = (item) => {
        (childResourcesByParentId.get(item.resourceId) || []).forEach((child) => {
          descendants.push(child);
          collect(child);
        });
      };
      if (resource) {
        collect(resource);
      }
      return descendants.some((item) => ["bay", "lane"].includes(item.type || ""));
    };
    return hasFiringControl;
  }, [childResourcesByParentId]);
  const lineFilterResources = useMemo(() => (
    resources
      .filter(resourceHasFiringControl)
      .filter((resource) => !resource.parentResourceId || resource.type === "bay")
      .sort((left, right) => (
        resourceTypeRank(left) - resourceTypeRank(right) ||
        String(left.name || "").localeCompare(String(right.name || ""))
      ))
  ), [resourceHasFiringControl, resources]);
  const visibleResources = useMemo(() => {
    const needle = boardQuery.trim().toLowerCase();
    const topResourceName = (resource) => {
      let current = resource;
      const visited = new Set();
      while (current?.parentResourceId && resourceById.has(current.parentResourceId) && !visited.has(current.parentResourceId)) {
        visited.add(current.resourceId);
        current = resourceById.get(current.parentResourceId);
      }
      return current?.name || resource.name || "";
    };
    const isDescendantOf = (resource, ancestorId) => {
      let current = resource;
      const visited = new Set();
      while (current?.parentResourceId && resourceById.has(current.parentResourceId) && !visited.has(current.resourceId)) {
        if (current.parentResourceId === ancestorId) {
          return true;
        }
        visited.add(current.resourceId);
        current = resourceById.get(current.parentResourceId);
      }
      return false;
    };
    const matchesResourceFilter = (resource) => (
      !boardResourceFilter ||
      resource.resourceId === boardResourceFilter ||
      isDescendantOf(resource, boardResourceFilter)
    );
    const defaultBoard = !needle && !boardResourceFilter;
    return resources
      .filter(resourceHasFiringControl)
      .filter((resource) => !defaultBoard || !resource.parentResourceId)
      .filter(matchesResourceFilter)
      .filter((resource) => !needle || [
        resource.name,
        resource.type,
        resource.status,
        resource.targetControl?.status,
        resource.attributes?.notes
      ].join(" ").toLowerCase().includes(needle))
      .sort((left, right) => (
        topResourceName(left).localeCompare(topResourceName(right)) ||
        resourceTypeRank(left) - resourceTypeRank(right) ||
        String(left.name || "").localeCompare(String(right.name || ""))
      ))
      .slice(0, defaultBoard ? 60 : 36);
  }, [boardQuery, boardResourceFilter, resourceById, resourceHasFiringControl, resources]);
  const lineStatusGroups = useMemo(() => {
    const groups = new Map();
    const addGroup = (groupResource, resource) => {
      if (!groupResource || !resource) {
        return;
      }
      const group = groups.get(groupResource.resourceId) || {
        groupResource,
        resources: []
      };
      if (!group.resources.some((item) => item.resourceId === resource.resourceId)) {
        group.resources.push(resource);
      }
      groups.set(groupResource.resourceId, group);
    };
    const topControlResource = (resource) => {
      let current = resource;
      const visited = new Set();
      while (current?.parentResourceId && resourceById.has(current.parentResourceId) && !visited.has(current.parentResourceId)) {
        visited.add(current.resourceId);
        current = resourceById.get(current.parentResourceId);
      }
      return current || resource;
    };

    visibleResources.forEach((resource) => {
      const containingResource = topControlResource(resource);
      addGroup(containingResource, containingResource);
      addGroup(containingResource, resource);
    });

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        resources: group.resources.sort((left, right) => {
          if (left.resourceId === group.groupResource.resourceId) {
            return -1;
          }
          if (right.resourceId === group.groupResource.resourceId) {
            return 1;
          }
          return resourceTypeRank(left) - resourceTypeRank(right) ||
            String(left.name || "").localeCompare(String(right.name || ""));
        })
      }))
      .sort((left, right) => (
        resourceTypeRank(left.groupResource) - resourceTypeRank(right.groupResource) ||
        String(left.groupResource.name || "").localeCompare(String(right.groupResource.name || ""))
      ));
  }, [resourceById, visibleResources]);
  const cycleResources = useMemo(() => (
    resources
      .filter((resource) => {
        if (!["area", "bay", "lane"].includes(resource.type || "")) {
          return false;
        }
        if (["bay", "lane"].includes(resource.type || "")) {
          return true;
        }
        const descendants = [];
        const collect = (item) => {
          (childResourcesByParentId.get(item.resourceId) || []).forEach((child) => {
            descendants.push(child);
            collect(child);
          });
        };
        collect(resource);
        return descendants.some((item) => ["bay", "lane"].includes(item.type || ""));
      })
      .sort((left, right) => String(left.name || "").localeCompare(String(right.name || "")))
  ), [childResourcesByParentId, resources]);
  const cyclePickerSections = useMemo(() => {
    const query = searchTokens(cyclePickerQuery);
    const sectionMap = new Map();
    const rootResourceFor = (resource) => {
      let current = resource;
      const visited = new Set();
      while (current?.parentResourceId && resourceById.has(current.parentResourceId) && !visited.has(current.resourceId)) {
        visited.add(current.resourceId);
        current = resourceById.get(current.parentResourceId);
      }
      return current || resource;
    };
    const matchesQuery = (resource) => {
      if (!query.compact && !query.spaced) {
        return true;
      }
      const haystack = searchTokens([
        resource.name,
        resource.type,
        resource.status,
        resource.targetControl?.status,
        formatCycle(resource)
      ].join(" "));
      return Boolean(
        (query.spaced && haystack.spaced.includes(query.spaced)) ||
        (query.compact && haystack.compact.includes(query.compact))
      );
    };

    cycleResources.forEach((resource) => {
      const sectionResource = rootResourceFor(resource);
      const section = sectionMap.get(sectionResource.resourceId) || {
        sectionResource,
        items: []
      };
      section.items.push(resource);
      sectionMap.set(sectionResource.resourceId, section);
    });

    return Array.from(sectionMap.values())
      .map((section) => {
        const itemIds = new Set(section.items.map((item) => item.resourceId));
        const sectionItem = section.items.find((item) => item.resourceId === section.sectionResource.resourceId);
        const bayItems = section.items
          .filter((item) => item.type === "bay")
          .sort((left, right) => String(left.name || "").localeCompare(String(right.name || "")))
          .map((bay) => ({
            bay,
            positions: (childResourcesByParentId.get(bay.resourceId) || [])
              .filter((position) => position.type === "lane" && itemIds.has(position.resourceId))
              .sort((left, right) => String(left.name || "").localeCompare(String(right.name || "")))
          }));
        const bayIds = new Set(bayItems.map((item) => item.bay.resourceId));
        const standalonePositions = section.items
          .filter((item) => item.type === "lane" && (!item.parentResourceId || !bayIds.has(item.parentResourceId)))
          .sort((left, right) => String(left.name || "").localeCompare(String(right.name || "")));
        const sectionMatches = sectionItem && matchesQuery(sectionItem);
        const visibleBays = bayItems
          .map((item) => {
            const bayMatches = matchesQuery(item.bay);
            const visiblePositions = item.positions.filter(matchesQuery);
            if (bayMatches || visiblePositions.length > 0) {
              return {
                ...item,
                positions: bayMatches ? item.positions : visiblePositions
              };
            }
            return null;
          })
          .filter(Boolean);
        const visibleStandalonePositions = standalonePositions.filter(matchesQuery);
        return {
          ...section,
          sectionItem: sectionMatches ? sectionItem : null,
          bayItems: visibleBays,
          standalonePositions: visibleStandalonePositions,
          visibleCount: (sectionMatches ? 1 : 0) +
            visibleBays.reduce((total, item) => total + 1 + item.positions.length, 0) +
            visibleStandalonePositions.length
        };
      })
      .filter((section) => section.visibleCount > 0)
      .sort((left, right) => (
        resourceTypeRank(left.sectionResource) - resourceTypeRank(right.sectionResource) ||
        String(left.sectionResource.name || "").localeCompare(String(right.sectionResource.name || ""))
      ));
  }, [childResourcesByParentId, cyclePickerQuery, cycleResources, resourceById]);
  const selectedCycleResource = useMemo(() => (
    cycleResources.find((resource) => resource.resourceId === cycleResourceId) ||
    cycleResources.find((resource) => resource.targetControl?.cycle?.enabled) ||
    cycleResources[0] ||
    null
  ), [cycleResourceId, cycleResources]);
  const selectedCycleState = selectedCycleResource ? cycleState(selectedCycleResource) : null;
  const shooterRows = useMemo(() => (
    activeReservations.map((reservation) => {
      const member = reservationMember(reservation, members);
      return {
        reservation,
        member,
        warnings: memberEligibility(member),
        resourceName: reservationResourceName(reservation, resourceById)
      };
    })
  ), [activeReservations, members, resourceById]);
  const filteredMembers = useMemo(() => {
    const needle = memberQuery.trim().toLowerCase();
    const sorted = [...members].sort((left, right) => {
      if (left.status === "active" && right.status !== "active") {
        return -1;
      }
      if (left.status !== "active" && right.status === "active") {
        return 1;
      }
      return String(left.fullName || left.email || "").localeCompare(String(right.fullName || right.email || ""));
    });
    if (!needle) {
      return sorted.slice(0, 10);
    }
    return sorted
      .filter((member) => [
        member.fullName,
        member.email,
        member.memberId,
        member.status
      ].join(" ").toLowerCase().includes(needle))
      .slice(0, 16);
  }, [memberQuery, members]);
  const selectedMember =
    members.find((member) => member.memberId === selectedMemberId) ||
    filteredMembers.find((member) => member.status === "active") ||
    filteredMembers[0] ||
    null;
  const selectedMemberReservations = todaysReservations
    .filter((reservation) => reservation.memberId === selectedMember?.memberId || reservation.contactEmail === selectedMember?.email)
    .slice(0, 6);
  const resourcesNeedingAction = resources.filter((resource) => resource.status !== "open" || resource.targetControl?.status !== "firing_allowed");
  const flaggedMembers = useMemo(() => members
    .filter((member) => (member.flags?.items || []).length > 0)
    .sort((left, right) => Number(right.flags?.criticalCount || 0) - Number(left.flags?.criticalCount || 0)), [members]);
  const activeFlags = useMemo(() => flaggedMembers.flatMap((member) => (member.flags?.items || [])
    .map((flag) => ({ ...flag, member }))
    .filter((flag) => flag.status !== "resolved")), [flaggedMembers]);
  const criticalFlags = activeFlags.filter((flag) => flag.severity === "critical");
  const trackedAssets = useMemo(() => resources.filter((resource) => (
    resource.attributes?.assetClass ||
    resource.attributes?.serialNumber ||
    (resource.attributes?.rentalStatus && resource.attributes.rentalStatus !== "not_rental")
  )), [resources]);
  const rentalAssets = trackedAssets.filter((resource) => resource.attributes?.rentalStatus && resource.attributes.rentalStatus !== "not_rental");
  const maintenanceAssets = trackedAssets.filter((resource) => (
    resource.status === "maintenance" || resource.attributes?.rentalStatus === "maintenance"
  ));
  const nextCeaseFire = visibleResources
    .map((resource) => ({ resource, cycle: cycleState(resource) }))
    .find((item) => ["active", "warning"].includes(item.cycle.tone));
  const renderCycleResourceButton = (resource, className = "") => {
    const state = cycleState(resource);
    const isSelected = resource.resourceId === selectedCycleResource?.resourceId;
    const includedCount = childResourcesByParentId.get(resource.resourceId)?.filter((item) => item.type === "lane").length || 0;
    return (
      <button
        aria-pressed={isSelected}
        className={`range-cycle-resource-option ${className} ${isSelected ? "is-selected" : ""}`}
        key={resource.resourceId}
        onClick={() => {
          setCycleResourceId(resource.resourceId);
          setCyclePickerOpen(false);
          setCyclePickerQuery("");
        }}
        type="button"
      >
        <span>
          <strong>{resource.name}</strong>
          <small>{resourceControlLabel(resource, includedCount)}</small>
        </span>
        <em className={`status-badge status-${state.tone}`}>{state.label}</em>
      </button>
    );
  };

  const assignableResources = resources
    .filter((resource) => resource.status !== "maintenance" && resource.status !== "closed")
    .sort((left, right) => String(left.name || "").localeCompare(String(right.name || "")));
  const activeMembers = members
    .filter((member) => member.status === "active")
    .sort((left, right) => memberLabel(left).localeCompare(memberLabel(right)));

  useEffect(() => {
    if (!selectedCycleResource) {
      return;
    }
    const cycle = selectedCycleResource.targetControl?.cycle || {};
    setCycleResourceId(selectedCycleResource.resourceId);
    setCycleDraft({
      enabled: cycle.enabled !== false,
      firingMinutes: String(cycle.firingMinutes || 50),
      targetCheckMinutes: String(cycle.targetCheckMinutes || 10),
      startsAt: dateTimeInputFromValue(cycle.startsAt),
      note: cycle.note || selectedCycleResource.targetControl?.note || "Scheduled target-check rhythm"
    });
  }, [selectedCycleResource]);

  function affectedResources(resource) {
    const affected = [];
    const collect = (item) => {
      if (!item || affected.some((existing) => existing.resourceId === item.resourceId)) {
        return;
      }
      affected.push(item);
      (childResourcesByParentId.get(item.resourceId) || []).forEach(collect);
    };
    collect(resource);
    return affected;
  }

  function sectionCommandPatch(resource, patch) {
    const command = typeof patch === "function" ? patch(resource) : patch;
    if (!command?.targetControl) {
      return command;
    }
    const { cycle, updatedAt, ...targetControl } = command.targetControl;
    return {
      ...command,
      targetControl
    };
  }

  async function updateResourceStatus(resource, patch) {
    if (onUpdateResource) {
      setCommandingResourceId(resource.resourceId);
      try {
        const affected = affectedResources(resource);
        if (affected.length > 1 && onUpdateResourceSectionCommand) {
          const result = await onUpdateResourceSectionCommand(resource.resourceId, sectionCommandPatch(resource, patch));
          if (!result) {
            setExpandedLineGroups((current) => {
              const next = new Set(current);
              next.add(resource.resourceId);
              return next;
            });
            setCycleNotice(`Section command could not be completed for ${resource.name}.`);
            return;
          }
          const updatedCount = result?.summary?.affectedResourceCount || result?.resources?.length || affected.length;
          setCycleNotice(`${resource.name} updated across ${updatedCount} linked controls.`);
          return;
        }
        const results = await Promise.allSettled(affected.map((target) => {
          const nextPatch = typeof patch === "function" ? patch(target) : patch;
          return onUpdateResource(target.resourceId, nextPatch);
        }));
        const failed = results
          .map((result, index) => ({ result, resource: affected[index] }))
          .filter(({ result }) => result.status === "rejected" || !result.value);
        if (failed.length > 0) {
          const failedNames = failed
            .slice(0, 3)
            .map(({ resource: item }) => item.name)
            .join(", ");
          const extraCount = failed.length > 3 ? ` and ${failed.length - 3} more` : "";
          setExpandedLineGroups((current) => {
            const next = new Set(current);
            next.add(resource.resourceId);
            return next;
          });
          setCycleNotice(`${affected.length - failed.length} of ${affected.length} controls updated. Check ${failedNames}${extraCount}.`);
          return;
        }
        setCycleNotice(`${resource.name} updated${affected.length > 1 ? ` across ${affected.length} linked items` : ""}.`);
      } finally {
        setCommandingResourceId((current) => (current === resource.resourceId ? "" : current));
      }
    }
  }

  function applyCyclePreset(firingMinutes, targetCheckMinutes) {
    setCycleDraft((current) => ({
      ...current,
      enabled: true,
      firingMinutes: String(firingMinutes),
      targetCheckMinutes: String(targetCheckMinutes)
    }));
  }

  async function saveCeaseFireCycle(event) {
    event.preventDefault();
    if (!selectedCycleResource || !onUpdateResource) {
      return;
    }
    const firingMinutes = Math.max(1, Number.parseInt(cycleDraft.firingMinutes || "0", 10));
    const targetCheckMinutes = Math.max(1, Number.parseInt(cycleDraft.targetCheckMinutes || "0", 10));
    const startsAt = cycleDraft.startsAt
      ? new Date(cycleDraft.startsAt).toISOString()
      : new Date().toISOString();

    await onUpdateResource(selectedCycleResource.resourceId, {
      targetControl: {
        ...(selectedCycleResource.targetControl || {}),
        status: selectedCycleResource.targetControl?.status || "firing_allowed",
        note: cycleDraft.note || "Scheduled target-check rhythm",
        cycle: {
          enabled: true,
          firingMinutes,
          targetCheckMinutes,
          startsAt,
          note: cycleDraft.note || ""
        }
      }
    });
    setCycleDraft((current) => ({ ...current, enabled: true }));
    setCycleNotice(`Rhythm saved for ${selectedCycleResource.name}.`);
  }

  async function disableCeaseFireCycle() {
    if (!selectedCycleResource || !onUpdateResource) {
      return;
    }
    await onUpdateResource(selectedCycleResource.resourceId, {
      targetControl: {
        ...(selectedCycleResource.targetControl || {}),
        cycle: {
          ...(selectedCycleResource.targetControl?.cycle || {}),
          enabled: false
        }
      }
    });
    setCycleDraft((current) => ({ ...current, enabled: false }));
    setCycleNotice(`Cease-fire rhythm disabled for ${selectedCycleResource.name}.`);
  }

  function defaultAssignmentStart() {
    const startsAt = new Date();
    startsAt.setMinutes(0, 0, 0);
    startsAt.setHours(startsAt.getHours() + 1);
    startsAt.setMinutes(startsAt.getMinutes() - startsAt.getTimezoneOffset());
    return startsAt.toISOString().slice(0, 16);
  }

  async function assignSelectedMember(event) {
    event.preventDefault();
    if (!selectedMember || !assignmentForm.resourceId || !onCreateReservation) {
      return;
    }
    const startsAt = new Date(assignmentForm.startsAt || defaultAssignmentStart());
    const endsAt = new Date(startsAt);
    endsAt.setMinutes(endsAt.getMinutes() + Number.parseInt(assignmentForm.durationMinutes || "60", 10));
    const resource = resources.find((item) => item.resourceId === assignmentForm.resourceId);
    await onCreateReservation({
      resourceId: assignmentForm.resourceId,
      memberId: selectedMember.memberId,
      customerName: memberLabel(selectedMember),
      contactEmail: selectedMember.email || "",
      startsAt: startsAt.toISOString(),
      endsAt: endsAt.toISOString(),
      notes: assignmentForm.notes || "Assigned from Range Operations.",
      overrideAvailability: true,
      overrideReason: "Range owner assignment from live operations.",
      source: "owner",
      status: "confirmed"
    });
    setOpsLog((current) => [
      {
        id: `${Date.now()}-${current.length}`,
        createdAt: new Date().toISOString(),
        memberName: memberLabel(selectedMember),
        action: "resource_assignment",
        resourceName: resource?.name || assignmentForm.resourceId,
        note: assignmentForm.notes
      },
      ...current
    ].slice(0, 12));
    setAssignmentForm((current) => ({ ...current, notes: "" }));
  }

  function reviewSafetyFlag(flag) {
    setSelectedMemberId(flag.member?.memberId || "");
    setSafetyActionId(flag.flagId || `${flag.member?.memberId}-${flag.title}`);
    switchOpsSection("shooters");
  }

  function sendSafetyToCloseout(flag) {
    setActionForm({
      action: "incident_note",
      resourceId: "",
      memberId: flag.member?.memberId || "",
      note: `Follow-up on ${flag.title || statusLabel(flag.type)}`
    });
    switchOpsSection("closeout");
  }

  function createSafetyRecord(event) {
    event.preventDefault();
    if (!safetyForm.memberId || !safetyForm.title.trim() || !onAddMemberFlag) {
      return;
    }
    onAddMemberFlag(safetyForm.memberId, {
      type: safetyForm.type,
      severity: safetyForm.severity,
      status: "active",
      title: safetyForm.title,
      notes: safetyForm.notes
    });
    setSafetyForm((current) => ({ ...current, title: "", notes: "" }));
  }

  function updateEquipmentAssignment(resource, memberId) {
    setEquipmentMemberAssignments((current) => ({
      ...current,
      [resource.resourceId]: memberId
    }));
  }

  function updateRentalStatus(resource, rentalStatus, extraAttributes = {}) {
    if (!onUpdateResource) {
      return;
    }
    onUpdateResource(resource.resourceId, {
      attributes: {
        ...(resource.attributes || {}),
        rentalStatus,
        ...extraAttributes
      }
    });
  }

  function sendEquipmentToMaintenance(resource) {
    const event = {
      id: `${Date.now()}-${resource.resourceId}`,
      createdAt: new Date().toISOString(),
      source: "Range Operations",
      title: "Moved to maintenance",
      note: "Marked from the daily equipment board."
    };
    if (onUpdateResource) {
      onUpdateResource(resource.resourceId, {
        status: "maintenance",
        attributes: {
          ...(resource.attributes || {}),
          rentalStatus: "maintenance",
          checkedOutMemberId: null,
          checkedOutMemberName: null,
          checkedOutAt: null,
          maintenanceLastActionAt: event.createdAt,
          maintenanceLastActionSource: event.source,
          maintenanceEvents: [event, ...(resource.attributes?.maintenanceEvents || [])].slice(0, 12)
        }
      });
    }
    setEquipmentAssignments((current) => {
      const next = { ...current };
      delete next[resource.resourceId];
      return next;
    });
    setEquipmentMemberAssignments((current) => {
      const next = { ...current };
      delete next[resource.resourceId];
      return next;
    });
    setOpsLog((current) => [
      {
        id: event.id,
        createdAt: event.createdAt,
        memberName: "Maintenance",
        action: "equipment_maintenance",
        resourceName: resource.name,
        note: event.note
      },
      ...current
    ].slice(0, 12));
  }

  function returnEquipment(resource) {
    if (onUpdateResource) {
      onUpdateResource(resource.resourceId, {
        status: "open",
        attributes: {
          ...(resource.attributes || {}),
          rentalStatus: "available",
          checkedOutMemberId: null,
          checkedOutMemberName: null,
          checkedOutAt: null
        }
      });
    }
    setEquipmentAssignments((current) => {
      const next = { ...current };
      delete next[resource.resourceId];
      return next;
    });
    setEquipmentMemberAssignments((current) => {
      const next = { ...current };
      delete next[resource.resourceId];
      return next;
    });
  }

  function checkoutEquipment(resource) {
    const memberId = equipmentMemberAssignments[resource.resourceId] || "";
    const member = members.find((item) => item.memberId === memberId);
    setEquipmentAssignments((current) => ({
      ...current,
      [resource.resourceId]: member ? memberLabel(member) : "Unassigned"
    }));
    updateRentalStatus(resource, "checked_out", {
      checkedOutMemberId: memberId || undefined,
      checkedOutMemberName: member ? memberLabel(member) : undefined,
      checkedOutAt: new Date().toISOString()
    });
  }

  async function recordAction(event) {
    event.preventDefault();
    if (!actionForm.note.trim()) {
      return;
    }
    const relatedMember = members.find((member) => member.memberId === actionForm.memberId);
    const resource = resources.find((item) => item.resourceId === actionForm.resourceId);
    const savedNote = await onCreateOperationNote?.({
      action: actionForm.action,
      resourceId: actionForm.resourceId,
      resourceName: resource?.name || "",
      memberId: actionForm.memberId,
      memberName: relatedMember ? memberLabel(relatedMember) : "Range shift",
      note: actionForm.note
    });
    if (!savedNote) {
      return;
    }
    if (actionForm.action === "incident_note" && relatedMember && onAddMemberFlag) {
      onAddMemberFlag(relatedMember.memberId, {
        type: "incident",
        severity: "warning",
        status: "active",
        title: actionForm.note || "Range officer incident note",
        notes: actionForm.note
      });
    }
    setOpsLog((current) => [
      {
        id: savedNote.noteId || `${Date.now()}-${current.length}`,
        createdAt: savedNote.createdAt || new Date().toISOString(),
        memberName: savedNote.memberName || (relatedMember ? memberLabel(relatedMember) : "Range shift"),
        action: savedNote.action || actionForm.action,
        resourceName: savedNote.resourceName || resource?.name || "",
        note: savedNote.note || actionForm.note
      },
      ...current
    ].slice(0, 12));
    setActionForm((current) => ({ ...current, note: "" }));
  }

  function switchOpsSection(sectionId) {
    const nextSection = normalizeOpsSection(sectionId);
    setActiveOpsSection(nextSection);
    window.history.replaceState(null, "", `${window.location.pathname}#operations-${nextSection}`);
  }

  const opsSections = [
    { id: "board", label: "Range board", meta: `${resourcesNeedingAction.length} needs action` },
    { id: "shooters", label: "Shooters", meta: `${activeReservationsToday.length} reservations` },
    { id: "safety", label: "Safety records", meta: `${activeFlags.length} open` },
    { id: "equipment", label: "Equipment", meta: `${trackedAssets.length} assets` },
    { id: "closeout", label: "Shift notes", meta: `${opsLog.length} recorded` }
  ];
  const activeSectionCopy = {
    board: {
      kicker: "Live command",
      title: "Range board",
      description: "Open, hold, pause, or recover resources without mixing shooter lookup and record keeping into the same workspace.",
      metrics: [
        { label: "Resources visible", value: visibleResources.length, detail: "filtered by resource/search" },
        { label: "Need action", value: resourcesNeedingAction.length, detail: "closed, held, maintenance, or cease-fire" },
        { label: "Next cycle", value: nextCeaseFire ? nextCeaseFire.cycle.label : "None", detail: nextCeaseFire ? nextCeaseFire.resource.name : "No configured cycle visible" }
      ]
    },
    shooters: {
      kicker: "Roster control",
      title: "Shooter check-in",
      description: "Work from reservations, eligibility warnings, member lookup, and check-in actions without leaving operations.",
      metrics: [
        { label: "Reservations", value: activeReservationsToday.length, detail: "active today" },
        { label: "Roster rows", value: shooterRows.length, detail: "visible check-in rows" },
        { label: "Selected shooter", value: selectedMember ? memberLabel(selectedMember) : "None", detail: selectedMember?.status || "No member selected" }
      ]
    },
    safety: {
      kicker: "Risk watch",
      title: "Safety and compliance records",
      description: "Review open incident and safety notes that may affect whether someone can shoot today.",
      metrics: [
        { label: "Open records", value: activeFlags.length, detail: "unresolved member flags" },
        { label: "Critical", value: criticalFlags.length, detail: "needs leadership attention" },
        { label: "Members flagged", value: flaggedMembers.length, detail: "with saved records" }
      ]
    },
    equipment: {
      kicker: "Readiness",
      title: "Equipment readiness",
      description: "Operate rental or tagged assets used during the day, while durable facility details stay in Range Facilities.",
      metrics: [
        { label: "Tracked assets", value: trackedAssets.length, detail: "tagged or rental resources" },
        { label: "Rental assets", value: rentalAssets.length, detail: "available, out, or maintenance" },
        { label: "Maintenance", value: maintenanceAssets.length, detail: "needs attention" }
      ]
    },
    closeout: {
      kicker: "Shift record",
      title: "Shift notes",
      description: "Record exceptions, incident follow-ups, cease-fire notes, and handoff notes that need to survive the shift.",
      metrics: [
        { label: "Saved notes", value: operationNotes.length, detail: operationNotesLoading ? "loading saved notes" : "durable shift records" },
        { label: "Today", value: operatingDateLabel, detail: "current operating day" }
      ]
    }
  }[activeOpsSection];

  return (
    <div className="ops-page">
      {loading && <div className="status-strip">Loading range operations...</div>}
      {cycleNotice && (
        <div className="status-strip" role="status">
          {cycleNotice}
        </div>
      )}

      {activeOpsSection === "board" && (
      <section className="module-metrics">
        <div>
          <span>Line reservations</span>
          <strong>{activeReservationsToday.length}</strong>
        </div>
        <div>
          <span>Active holds</span>
          <strong>{activeBlocks.length}</strong>
        </div>
        <div>
          <span>Resources needing action</span>
          <strong>{resourcesNeedingAction.length}</strong>
        </div>
        <div>
          <span>Safety records</span>
          <strong>{activeFlags.length}</strong>
          <small>{criticalFlags.length} critical</small>
        </div>
        <div>
          <span>Equipment assets</span>
          <strong>{trackedAssets.length}</strong>
          <small>{rentalAssets.length} rental / {maintenanceAssets.length} maintenance</small>
        </div>
        <div>
          <span>Next cycle</span>
          <strong>{nextCeaseFire ? nextCeaseFire.cycle.label : "None"}</strong>
          <small>{nextCeaseFire ? `${nextCeaseFire.resource.name} / ${nextCeaseFire.cycle.detail}` : "No configured cycle visible"}</small>
        </div>
      </section>
      )}

      <nav className="console-section-nav page-section-nav operations-tabs" aria-label="Range Operations sections">
        {opsSections.map((section) => (
          <button
            type="button"
            className={activeOpsSection === section.id ? "is-active" : ""}
            key={section.id}
            onClick={() => switchOpsSection(section.id)}
          >
            <span>{section.label}</span>
            <small>{section.meta}</small>
          </button>
        ))}
      </nav>

      {activeOpsSection !== "board" && (
        <section className="membership-section-overview operations-section-overview">
          <div>
            <span className="section-kicker">{activeSectionCopy.kicker}</span>
            <h3>{activeSectionCopy.title}</h3>
            <p>{activeSectionCopy.description}</p>
          </div>
          <div className="membership-context-metrics operations-context-metrics">
            {activeSectionCopy.metrics.map((metric) => (
              <div key={metric.label}>
                <span>{metric.label}</span>
                <strong>{metric.value}</strong>
                <small>{metric.detail}</small>
              </div>
            ))}
          </div>
        </section>
      )}

      {activeOpsSection === "board" && (
        <section className="panel range-control-live-board">
          <div className="panel-header">
            <div>
              <h2>Live line control</h2>
              <p>Focused controls for the current operating day.</p>
            </div>
          </div>
          <div className="operations-inline-filter">
            <label>
              Resource
              <select value={boardResourceFilter} onChange={(event) => setBoardResourceFilter(event.target.value)}>
                <option value="">All range sections</option>
                {lineFilterResources.map((resource) => (
                  <option value={resource.resourceId} key={resource.resourceId}>{resource.name}</option>
                ))}
              </select>
            </label>
            <label>
              Search resources
              <input value={boardQuery} onChange={(event) => setBoardQuery(event.target.value)} placeholder="Resource, status, note" />
            </label>
          </div>
          <section className="range-control-cycle-panel" aria-label="Cease-fire cycle setup">
            <div className="range-control-cycle-summary">
              <span className="section-kicker">Cease-fire rhythm</span>
              <h3>{selectedCycleResource?.name || "Select a resource"}</h3>
              <p>
                Set the hot range and target-check cadence operators use during the day. Use Range Facilities for durable resource identity and booking rules.
              </p>
              {selectedCycleState && (
                <div className={`range-cycle-state is-${selectedCycleState.tone}`}>
                  <strong>{selectedCycleState.label}</strong>
                  <span>{selectedCycleState.detail}</span>
                </div>
              )}
            </div>
            {selectedCycleResource ? (
            <form className={`range-cycle-form ${cycleDraft.enabled ? "is-enabled" : "is-disabled"}`} onSubmit={saveCeaseFireCycle}>
              <div className={`range-cycle-resource-picker ${cyclePickerOpen ? "is-open" : "is-collapsed"}`}>
                <div className="range-cycle-picker-heading">
                  <div>
                    <span>Resource</span>
                    <strong>Choose where this rhythm applies</strong>
                  </div>
                  <button
                    className="secondary-button range-cycle-picker-toggle"
                    onClick={() => setCyclePickerOpen((current) => !current)}
                    type="button"
                  >
                    {cyclePickerOpen ? "Close picker" : "Change"}
                  </button>
                </div>
                <div className="range-cycle-selected-resource">
                  <div>
                    <strong>{selectedCycleResource.name}</strong>
                    <span>{resourceControlLabel(selectedCycleResource, childResourcesByParentId.get(selectedCycleResource.resourceId)?.filter((item) => item.type === "lane").length || 0)} / {formatCycle(selectedCycleResource)}</span>
                  </div>
                  {selectedCycleState && <em className={`status-badge status-${selectedCycleState.tone}`}>{selectedCycleState.label}</em>}
                </div>
                {cyclePickerOpen && (
                  <>
                    <label className="range-cycle-picker-search">
                      Search
                      <input
                        value={cyclePickerQuery}
                        onChange={(event) => setCyclePickerQuery(event.target.value)}
                        onFocus={() => setCycleNotice("")}
                        placeholder="Bay zone, firing position, section"
                      />
                    </label>
                    <div className="range-cycle-picker-list">
                      {cyclePickerSections.map((section) => (
                        <section className="range-cycle-picker-section" key={section.sectionResource.resourceId}>
                          <div className="range-cycle-picker-section-header">
                            <span>{resourceControlLabel(section.sectionResource, section.visibleCount)}</span>
                            <strong>{section.sectionResource.name}</strong>
                          </div>
                          {section.sectionItem && renderCycleResourceButton(section.sectionItem, "is-section")}
                          {section.bayItems.map(({ bay, positions }) => (
                            <div className="range-cycle-picker-bay" key={bay.resourceId}>
                              {renderCycleResourceButton(bay, "is-bay")}
                              {positions.length > 0 && (
                                <div className="range-cycle-picker-positions">
                                  {positions.map((position) => renderCycleResourceButton(position, "is-position"))}
                                </div>
                              )}
                            </div>
                          ))}
                          {section.standalonePositions.length > 0 && (
                            <div className="range-cycle-picker-positions is-standalone">
                              {section.standalonePositions.map((position) => renderCycleResourceButton(position, "is-position"))}
                            </div>
                          )}
                        </section>
                      ))}
                      {cyclePickerSections.length === 0 && (
                        <div className="range-cycle-picker-empty">
                          <strong>No matching resources</strong>
                          <span>Try a bay zone, firing position, or range section name.</span>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              <div className="range-cycle-visual">
                <div
                  style={{
                    "--hot-share": `${Math.max(8, Math.min(92, (Number(cycleDraft.firingMinutes || 0) / Math.max(1, Number(cycleDraft.firingMinutes || 0) + Number(cycleDraft.targetCheckMinutes || 0))) * 100))}%`
                  }}
                >
                  <span>Hot range</span>
                  <span>Cease-fire</span>
                </div>
              </div>
              <div className={`range-cycle-mode-banner ${cycleDraft.enabled ? "is-enabled" : "is-disabled"}`}>
                <strong>{cycleDraft.enabled ? "Cycle will run" : "Cycle is disabled"}</strong>
                <span>
                  {cycleDraft.enabled
                    ? "Saving keeps this resource on the displayed hot range and cease-fire rhythm."
                    : "The displayed values are preserved. Saving now will enable this rhythm again."}
                </span>
              </div>
              <div className="range-cycle-input-grid">
                <label>
                  Hot minutes
                  <input
                    type="number"
                    min="1"
                    value={cycleDraft.firingMinutes}
                    onChange={(event) => setCycleDraft((current) => ({ ...current, firingMinutes: event.target.value }))}
                  />
                </label>
                <label>
                  Cease-fire minutes
                  <input
                    type="number"
                    min="1"
                    value={cycleDraft.targetCheckMinutes}
                    onChange={(event) => setCycleDraft((current) => ({ ...current, targetCheckMinutes: event.target.value }))}
                  />
                </label>
                <label>
                  Starts
                  <input
                    type="datetime-local"
                    value={cycleDraft.startsAt}
                    onChange={(event) => setCycleDraft((current) => ({ ...current, startsAt: event.target.value }))}
                  />
                </label>
              </div>
              <label className="range-cycle-note">
                Operator note
                <input
                  value={cycleDraft.note}
                  onChange={(event) => setCycleDraft((current) => ({ ...current, note: event.target.value }))}
                  placeholder="Cease fire every hour for target checks"
                />
              </label>
              <div className="range-cycle-presets">
                <button type="button" className="secondary-button" onClick={() => applyCyclePreset(50, 10)}>50 / 10</button>
                <button type="button" className="secondary-button" onClick={() => applyCyclePreset(45, 15)}>45 / 15</button>
                <button type="button" className="secondary-button" onClick={() => applyCyclePreset(30, 5)}>30 / 5</button>
              </div>
              <div className="form-actions">
                <button type="submit" disabled={!selectedCycleResource || !onUpdateResource || updatingResourceId === selectedCycleResource?.resourceId}>
                  {cycleDraft.enabled ? "Save rhythm" : "Enable and save rhythm"}
                </button>
                <button type="button" className="secondary-button" disabled={!selectedCycleResource || !onUpdateResource || !cycleDraft.enabled} onClick={disableCeaseFireCycle}>
                  Disable cycle
                </button>
              </div>
            </form>
            ) : (
              <div className="range-cycle-empty">
                <strong>No range resources loaded</strong>
                <span>Create or publish bay zones, firing positions, or range sections before setting an operating rhythm.</span>
              </div>
            )}
          </section>
          <div className="range-control-layout range-control-layout-single">
            <section className="range-control-board-section range-control-line-board">
              <div className="range-board-section-header">
                <div>
                  <h3>Line status</h3>
                  <p>Open, hold, pause, or recover bay zones and their included firing positions.</p>
                </div>
              </div>
              <div className="range-resource-list range-resource-group-list">
                {lineStatusGroups.map(({ groupResource, resources: groupResources }) => {
                  const rowResources = groupResources.filter((resource) => resource.resourceId !== groupResource.resourceId);
                  const sectionCommandResources = rowResources.length > 0 ? rowResources : groupResources;
                  const sectionFireState = aggregateTargetControlState(sectionCommandResources);
                  const filterIsActive = Boolean(boardResourceFilter || boardQuery.trim());
                  const groupExpanded = expandedLineGroups.has(groupResource.resourceId);
                  const descendantControls = [];
                  const collectDescendantControls = (resource) => {
                    (childResourcesByParentId.get(resource.resourceId) || []).forEach((child) => {
                      if (["bay", "lane"].includes(child.type || "")) {
                        descendantControls.push(child);
                      }
                      collectDescendantControls(child);
                    });
                  };
                  collectDescendantControls(groupResource);
                  const effectiveRowResources = filterIsActive
                    ? rowResources
                    : (groupExpanded ? descendantControls : []);
                  const rowResourceIds = new Set(effectiveRowResources.map((resource) => resource.resourceId));
                  const topControlRows = effectiveRowResources.filter((resource) => {
                    if (resource.type === "area") {
                      return false;
                    }
                    if (resource.type === "lane" && resource.parentResourceId && rowResourceIds.has(resource.parentResourceId)) {
                      return false;
                    }
                    return true;
                  });
                  const shownCount = filterIsActive
                    ? (rowResources.length || groupResources.length)
                    : (descendantControls.length || rowResources.length || groupResources.length);
                  const isGroupUpdating = updatingResourceId === groupResource.resourceId || Boolean(commandingResourceId);
                  const groupIncludedCount = childResourcesByParentId.get(groupResource.resourceId)?.length || rowResources.length;
                  return (
                    <section className={`range-resource-group range-resource-group--${resourceStatusTone(groupResource)}`} key={groupResource.resourceId}>
                      <div className="range-resource-group-header">
                        <div>
                          <span className="section-kicker">{resourceControlLabel(groupResource, groupIncludedCount)}</span>
                          <h4>{groupResource.name}</h4>
                          <p>{filterIsActive
                            ? `${shownCount} matching control${shownCount === 1 ? "" : "s"} in this section.`
                            : `${shownCount} live control${shownCount === 1 ? "" : "s"} in this section.`}</p>
                        </div>
                        <div className="range-resource-group-state">
                          <span className={`status-badge status-${resourceStatusTone(groupResource)}`}>{groupResource.status === "temporarily_reserved" ? "Reserved" : statusLabel(groupResource.status)}</span>
                          <span className={`status-badge status-${sectionFireState.tone}`}>{sectionFireState.label}</span>
                          {!filterIsActive && descendantControls.length > 0 && (
                            <button
                              className="secondary-button range-line-toggle"
                              onClick={() => setExpandedLineGroups((current) => {
                                const next = new Set(current);
                                if (next.has(groupResource.resourceId)) {
                                  next.delete(groupResource.resourceId);
                                } else {
                                  next.add(groupResource.resourceId);
                                }
                                return next;
                              })}
                              type="button"
                            >
                              {groupExpanded ? "Hide controls" : `Show controls (${descendantControls.length})`}
                            </button>
                          )}
                        </div>
                        <div className="range-resource-group-command">
                          <small>Section command applies to every included firing control.</small>
                          <div>
                            <label>
                              <span>Section state</span>
                              <select
                                value={groupResource.status || "open"}
                                disabled={isGroupUpdating}
                                onChange={(event) => updateResourceStatus(groupResource, { status: event.target.value })}
                              >
                                <option value="open">Open</option>
                                <option value="maintenance">Maintenance</option>
                                <option value="temporarily_reserved">Reserved</option>
                                <option value="closed">Closed</option>
                              </select>
                            </label>
                            <button className="control-button control-cease" type="button" disabled={isGroupUpdating} onClick={() => updateResourceStatus(groupResource, (target) => ({ targetControl: { ...(target.targetControl || {}), status: "target_check", note: "Cease fire / target check in progress", cycle: target.targetControl?.cycle } }))}>Cease section</button>
                            <button className="control-button control-hot" type="button" disabled={isGroupUpdating} onClick={() => updateResourceStatus(groupResource, (target) => ({ targetControl: { ...(target.targetControl || {}), status: "firing_allowed", note: "Live fire resumed", cycle: target.targetControl?.cycle } }))}>Resume section</button>
                          </div>
                        </div>
                      </div>
                      {topControlRows.length > 0 && (
                      <div className="range-resource-group-items">
                        {topControlRows.map((resource) => {
                          const cycle = cycleState(resource);
                          const isUpdating = updatingResourceId === resource.resourceId || Boolean(commandingResourceId);
                          const includedPositions = (childResourcesByParentId.get(resource.resourceId) || [])
                            .filter((child) => child.type === "lane" && rowResourceIds.has(child.resourceId));
                          const containedCount = includedPositions.length;
                          const bayFireState = aggregateTargetControlState(
                            includedPositions.length > 0 ? [resource, ...includedPositions] : [resource],
                            "Mixed position state"
                          );
                          return (
                            <article className={`range-resource-card range-bay-control-card range-resource-card--${resourceStatusTone(resource)}`} key={resource.resourceId}>
                              <div className="range-bay-control-main">
                                <div>
                                  <strong>{resource.name}</strong>
                                  <span>{resourceControlLabel(resource, containedCount)} / {formatCycle(resource)}</span>
                                  <span>{resource.availabilityPolicy?.openTime && resource.availabilityPolicy?.closeTime
                                    ? `${resource.availabilityPolicy.openTime}-${resource.availabilityPolicy.closeTime}`
                                    : "Uses range schedule"}</span>
                                </div>
                                <div className="range-state-stack">
                                  <span className={`status-badge status-${resourceStatusTone(resource)}`}>{resource.status === "temporarily_reserved" ? "Reserved" : statusLabel(resource.status)}</span>
                                  <span className={`status-badge status-${bayFireState.tone}`}>{bayFireState.label}</span>
                                  <small>{cycle.detail}</small>
                                </div>
                                <div className="range-control-actions">
                                  <label>
                                    <span>{resource.type === "lane" ? "Position state" : "Bay state"}</span>
                                    <select
                                      value={resource.status || "open"}
                                      disabled={isUpdating}
                                      onChange={(event) => updateResourceStatus(resource, { status: event.target.value })}
                                    >
                                      <option value="open">Open</option>
                                      <option value="maintenance">Maintenance</option>
                                      <option value="temporarily_reserved">Reserved</option>
                                      <option value="closed">Closed</option>
                                    </select>
                                  </label>
                                  {resource.targetControl?.status === "target_check" ? (
                                    <button className="control-button control-hot" type="button" disabled={isUpdating} onClick={() => updateResourceStatus(resource, (target) => ({ targetControl: { ...(target.targetControl || {}), status: "firing_allowed", note: "Live fire resumed", cycle: target.targetControl?.cycle } }))}>Resume fire</button>
                                  ) : (
                                    <button className="control-button control-cease" type="button" disabled={isUpdating} onClick={() => updateResourceStatus(resource, (target) => ({ targetControl: { ...(target.targetControl || {}), status: "target_check", note: "Cease fire / target check in progress", cycle: target.targetControl?.cycle } }))}>Cease fire</button>
                                  )}
                                </div>
                              </div>
                              {includedPositions.length > 0 && (
                                <div className="range-position-list">
                                  <div className="range-position-list-header">
                                    <span>Included firing positions</span>
                                    <small>{includedPositions.length} position{includedPositions.length === 1 ? "" : "s"} controlled by this bay zone</small>
                                  </div>
                                  {includedPositions.map((position) => {
                                    const positionCycle = cycleState(position);
                                    const isPositionUpdating = updatingResourceId === position.resourceId || Boolean(commandingResourceId);
                                    return (
                                      <div className="range-position-row" key={position.resourceId}>
                                        <div>
                                          <strong>{position.name}</strong>
                                          <span>{position.availabilityPolicy?.openTime && position.availabilityPolicy?.closeTime
                                            ? `${position.availabilityPolicy.openTime}-${position.availabilityPolicy.closeTime}`
                                            : "Uses bay schedule"}</span>
                                        </div>
                                        <div className="range-state-stack range-position-state">
                                          <span className={`status-badge status-${resourceStatusTone(position)}`}>{position.status === "temporarily_reserved" ? "Reserved" : statusLabel(position.status)}</span>
                                          <span className={`status-badge status-${positionCycle.tone}`}>{positionCycle.label}</span>
                                        </div>
                                        <div className="range-control-actions range-position-actions">
                                          <label>
                                            <span>Position state</span>
                                            <select
                                              value={position.status || "open"}
                                              disabled={isPositionUpdating}
                                              onChange={(event) => updateResourceStatus(position, { status: event.target.value })}
                                            >
                                              <option value="open">Open</option>
                                              <option value="maintenance">Maintenance</option>
                                              <option value="temporarily_reserved">Reserved</option>
                                              <option value="closed">Closed</option>
                                            </select>
                                          </label>
                                          {position.targetControl?.status === "target_check" ? (
                                            <button className="control-button control-hot" type="button" disabled={isPositionUpdating} onClick={() => updateResourceStatus(position, (target) => ({ targetControl: { ...(target.targetControl || {}), status: "firing_allowed", note: "Live fire resumed", cycle: target.targetControl?.cycle } }))}>Resume</button>
                                          ) : (
                                            <button className="control-button control-cease" type="button" disabled={isPositionUpdating} onClick={() => updateResourceStatus(position, (target) => ({ targetControl: { ...(target.targetControl || {}), status: "target_check", note: "Cease fire / target check in progress", cycle: target.targetControl?.cycle } }))}>Cease</button>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </article>
                          );
                        })}
                      </div>
                      )}
                    </section>
                  );
                })}
                {visibleResources.length === 0 && <div className="empty-state">No resources match this control view.</div>}
              </div>
            </section>
          </div>
        </section>
      )}

      {activeOpsSection === "shooters" && (
        <div className="operations-workbench-grid">
          <section className="panel operations-wide-panel">
            <div className="panel-header">
              <div>
                <h2>Shooter roster</h2>
                <p>Today's reservations with eligibility warnings and check-in actions.</p>
              </div>
            </div>
            <div className="operations-inline-filter">
              <label>
                Resource
                <select value={rosterResourceFilter} onChange={(event) => setRosterResourceFilter(event.target.value)}>
                  <option value="">All reservations today</option>
                  {resources.map((resource) => (
                    <option value={resource.resourceId} key={resource.resourceId}>{resource.name}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="range-shooter-list">
              {shooterRows.map(({ reservation, member, resourceName, warnings }) => (
                <article className="range-shooter-card" key={reservation.reservationId}>
                  <div>
                    <strong>{memberLabel(member) || reservation.customerName || "Unlinked shooter"}</strong>
                    <span>{resourceName} / {formatTime(reservation.startsAt)}-{formatTime(reservation.endsAt)}</span>
                    <span>{reservation.contactEmail || member?.email || "No contact email"}</span>
                  </div>
                  <div className="ops-checklist">
                    {warnings.map((warning) => (
                      <span key={warning} className={warning === "Eligible" ? "status-badge status-active" : "status-badge status-warning"}>
                        {warning}
                      </span>
                    ))}
                  </div>
                  <div className="row-actions compact-actions control-actions shooter-actions">
                    <button
                      className="control-button control-open"
                      type="button"
                      disabled={!onUpdateReservation || updatingReservationId === reservation.reservationId}
                      onClick={() => onUpdateReservation(reservation.reservationId, {
                        attendanceAction: "check_in",
                        overrideAvailability: true,
                        overrideReason: "Range control marked shooter in."
                      })}
                    >
                      In
                    </button>
                    <button className="control-button" type="button" disabled={!onUpdateReservation || updatingReservationId === reservation.reservationId} onClick={() => onUpdateReservation(reservation.reservationId, { attendanceAction: "check_out" })}>Out</button>
                    <button className="control-button control-maintenance" type="button" disabled={!onUpdateReservation || updatingReservationId === reservation.reservationId || reservation.status === "no_show"} onClick={() => onUpdateReservation(reservation.reservationId, { status: "no_show", reason: "Range control marked no-show." })}>No-show</button>
                  </div>
                </article>
              ))}
              {shooterRows.length === 0 && <div className="empty-state">No shooter reservations for this date.</div>}
            </div>
          </section>

          <section className="panel">
            <div className="panel-header">
              <div>
                <h2>Shooter lookup</h2>
                <p>Search by name, email, status, or member information.</p>
              </div>
            </div>
            <div className="operations-inline-filter">
              <label>
                Search members
                <input value={memberQuery} onChange={(event) => setMemberQuery(event.target.value)} placeholder="Name, email, status" />
              </label>
            </div>
            <div className="ops-member-list">
              {filteredMembers.map((member) => (
                <button type="button" key={member.memberId} className={member.memberId === selectedMember?.memberId ? "is-active" : ""} onClick={() => setSelectedMemberId(member.memberId)}>
                  <strong>{memberLabel(member)}</strong>
                  <span>{statusLabel(member.status || "unknown")} / {membershipPlanLabel(member, membershipTypes)}</span>
                </button>
              ))}
            </div>
          </section>

          <section className="panel">
            <div className="panel-header">
              <div>
                <h2>Eligibility detail</h2>
                <p>Waiver, qualification, guest-pass, and reservation context for the selected shooter.</p>
              </div>
            </div>
            {selectedMember ? (
              <>
                <div className="ops-identity">
                  <strong>{memberLabel(selectedMember)}</strong>
                  <span>{selectedMember.email}</span>
                </div>
                <div className="ops-checklist">
                  {memberEligibility(selectedMember).map((item) => (
                    <span key={item} className={item === "Eligible" ? "status-badge status-active" : "status-badge status-warning"}>{item}</span>
                  ))}
                </div>
                <dl className="ops-facts">
                  <div>
                    <dt>Guest passes</dt>
                    <dd>{selectedMember.guestPassLedger?.remaining || 0} remaining</dd>
                  </div>
                  <div>
                    <dt>Waiver expires</dt>
                    <dd>{formatDateTime(selectedMember.reviewSummary?.applicantWaiverExpiresAt)}</dd>
                  </div>
                  <div>
                    <dt>Reservations today</dt>
                    <dd>{selectedMemberReservations.length}</dd>
                  </div>
                </dl>
                <form className="operations-assignment-form" onSubmit={assignSelectedMember}>
                  <div className="requirements-policy-heading">
                    <span>Optional assignment</span>
                    <h4>Assign this member to a resource</h4>
                    <p>Use this for guest visits or owner-managed line placement when the member did not reserve a lane themselves.</p>
                  </div>
                  <label>
                    Resource
                    <select value={assignmentForm.resourceId} onChange={(event) => setAssignmentForm((current) => ({ ...current, resourceId: event.target.value }))}>
                      <option value="">Select resource</option>
                      {assignableResources.map((resource) => (
                        <option value={resource.resourceId} key={resource.resourceId}>{resource.name}</option>
                      ))}
                    </select>
                  </label>
                  <div className="form-row">
                    <label>
                      Start
                      <input type="datetime-local" value={assignmentForm.startsAt || defaultAssignmentStart()} onChange={(event) => setAssignmentForm((current) => ({ ...current, startsAt: event.target.value }))} />
                    </label>
                    <label>
                      Duration
                      <select value={assignmentForm.durationMinutes} onChange={(event) => setAssignmentForm((current) => ({ ...current, durationMinutes: event.target.value }))}>
                        <option value="30">30 minutes</option>
                        <option value="60">1 hour</option>
                        <option value="90">90 minutes</option>
                        <option value="120">2 hours</option>
                      </select>
                    </label>
                  </div>
                  <label>
                    Staff note
                    <input value={assignmentForm.notes} onChange={(event) => setAssignmentForm((current) => ({ ...current, notes: event.target.value }))} placeholder="Walk-in assignment, class overflow, owner override" />
                  </label>
                  <button type="submit" disabled={!onCreateReservation || !selectedMember || !assignmentForm.resourceId}>
                    Assign resource
                  </button>
                </form>
              </>
            ) : (
              <div className="empty-state">No members match this search.</div>
            )}
          </section>
        </div>
      )}

      {activeOpsSection === "safety" && (
        <section className="panel ops-support-panel">
          <div className="panel-header">
            <div>
              <h2>Safety and compliance watch</h2>
              <p>Create and review staff-only safety, incident, account, or admin records that can affect whether someone should shoot today.</p>
            </div>
          </div>
          <TaskDisclosure
            actionLabel="Add"
            title="Add safety or compliance record"
            description="Create records from staff observations, range officer decisions, or member-account concerns."
          >
            <form className="stacked-form" onSubmit={createSafetyRecord}>
              <label>
                Member
                <select value={safetyForm.memberId} onChange={(event) => setSafetyForm((current) => ({ ...current, memberId: event.target.value }))}>
                  <option value="">Select member</option>
                  {members.map((member) => (
                    <option value={member.memberId} key={member.memberId}>{memberLabel(member)}</option>
                  ))}
                </select>
              </label>
              <div className="form-row">
                <label>
                  Record type
                  <select value={safetyForm.type} onChange={(event) => setSafetyForm((current) => ({ ...current, type: event.target.value }))}>
                    <option value="incident">Incident</option>
                    <option value="safety">Safety</option>
                    <option value="account">Account</option>
                    <option value="admin">Admin</option>
                  </select>
                </label>
                <label>
                  Severity
                  <select value={safetyForm.severity} onChange={(event) => setSafetyForm((current) => ({ ...current, severity: event.target.value }))}>
                    <option value="info">Info</option>
                    <option value="warning">Warning</option>
                    <option value="critical">Critical</option>
                  </select>
                </label>
              </div>
              <label>
                Title
                <input value={safetyForm.title} onChange={(event) => setSafetyForm((current) => ({ ...current, title: event.target.value }))} placeholder="RSO intervention, account hold, safety concern" />
              </label>
              <label>
                Notes
                <textarea value={safetyForm.notes} onChange={(event) => setSafetyForm((current) => ({ ...current, notes: event.target.value }))} rows={4} />
              </label>
              <button type="submit" disabled={!safetyForm.memberId || !safetyForm.title.trim() || !onAddMemberFlag}>
                Add record
              </button>
            </form>
          </TaskDisclosure>
          <div className="ops-watch-list">
            {activeFlags.slice(0, 16).map((flag) => (
              <article className={`ops-watch-item ops-watch-item--${flag.severity || "info"}`} key={flag.flagId || `${flag.member?.memberId}-${flag.title}`}>
                <div>
                  <strong>{flag.title || statusLabel(flag.type)}</strong>
                  <span>{memberLabel(flag.member)} / {statusLabel(flag.type)} / {formatDateTime(flag.createdAt)}</span>
                  {safetyActionId === (flag.flagId || `${flag.member?.memberId}-${flag.title}`) && (
                    <small>Member selected in the Shooters tab for review.</small>
                  )}
                </div>
                <div className="ops-safety-actions">
                  <span className={flag.severity === "critical" ? "status-badge status-warning" : "status-badge status-muted"}>{statusLabel(flag.severity || "info")}</span>
                  <div className="row-actions compact-actions">
                    <button type="button" className="secondary-button" onClick={() => reviewSafetyFlag(flag)}>Open shooter</button>
                    <button type="button" onClick={() => sendSafetyToCloseout(flag)}>Create shift note</button>
                  </div>
                </div>
              </article>
            ))}
            {activeFlags.length === 0 && <div className="ops-compact-empty">No open safety or incident records.</div>}
          </div>
        </section>
      )}

      {activeOpsSection === "equipment" && (
        <section className="panel ops-support-panel">
          <div className="panel-header">
            <div>
              <h2>Rental checkout board</h2>
              <p>Rental and tagged assets that staff may need during the operating day.</p>
            </div>
          </div>
          <div className="ops-watch-list">
            {trackedAssets.map((resource) => {
              const rentalStatus = resource.attributes?.rentalStatus || "not_rental";
              const isCheckedOut = rentalStatus === "checked_out";
              const canCheckOut = rentalStatus === "available";
              const isUpdating = updatingResourceId === resource.resourceId;
              return (
                <article className="ops-watch-item ops-equipment-item" key={resource.resourceId}>
                  <div>
                    <strong>{resource.name}</strong>
                    <span>{resource.attributes?.assetClass || statusLabel(resource.type)} / {resource.attributes?.serialNumber || "No tag"} / Due {dateOnly(resource.attributes?.maintenanceDueAt)}</span>
                  </div>
                  <div className="ops-equipment-actions">
                    <span className={rentalStatus === "maintenance" ? "status-badge status-warning" : "status-badge status-muted"}>{statusLabel(rentalStatus)}</span>
                    {rentalStatus !== "not_rental" && (
                      <div className="ops-equipment-checkout">
                        {canCheckOut && (
                          <select value={equipmentMemberAssignments[resource.resourceId] || ""} onChange={(event) => updateEquipmentAssignment(resource, event.target.value)} aria-label={`Assign ${resource.name} to member`}>
                            <option value="">No member assigned</option>
                            {activeMembers.map((member) => (
                              <option value={member.memberId} key={member.memberId}>{memberLabel(member)}</option>
                            ))}
                          </select>
                        )}
                        {isCheckedOut && (
                          <small>
                            Checked out to {equipmentAssignments[resource.resourceId] || resource.attributes?.checkedOutMemberName || "Unassigned"}
                          </small>
                        )}
                        {!isCheckedOut && resource.attributes?.checkedOutMemberName && (
                          <small>Last checkout: {resource.attributes.checkedOutMemberName}</small>
                        )}
                        {resource.attributes?.maintenanceLastActionAt && (
                          <small>Last maintenance action: {formatDateTime(resource.attributes.maintenanceLastActionAt)}</small>
                        )}
                        {rentalStatus === "maintenance" && (
                          <small>Out of service. Return to available before checkout.</small>
                        )}
                      <div className="row-actions compact-actions">
                        <button type="button" className="secondary-button" disabled={isUpdating} onClick={() => returnEquipment(resource)}>Available</button>
                        <button type="button" className="secondary-button" disabled={isUpdating || !canCheckOut || isCheckedOut} onClick={() => checkoutEquipment(resource)}>Check out</button>
                        <button type="button" disabled={isUpdating || rentalStatus === "maintenance"} onClick={() => sendEquipmentToMaintenance(resource)}>
                          {rentalStatus === "maintenance" ? "In maintenance" : "Maintenance"}
                        </button>
                      </div>
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
            {trackedAssets.length === 0 && <div className="ops-compact-empty">No tagged equipment assets yet. Add facility asset tracking from Range Facilities.</div>}
          </div>
        </section>
      )}

      {activeOpsSection === "closeout" && (
        <section className="panel operations-closeout-panel">
          <div className="panel-header">
            <div>
              <h2>Shift notes</h2>
              <p>Record range officer actions and review saved operating notes.</p>
            </div>
          </div>
          <TaskDisclosure
            actionLabel="Record"
            title="Record an exception or shift note"
            description="This is not meant to duplicate every reservation or resource status change. Use it for staff notes, exceptions, incidents, and decisions that need a human-readable shift record."
          >
            <form className="stacked-form" onSubmit={recordAction}>
              <label>
                Action
                <select value={actionForm.action} onChange={(event) => setActionForm((current) => ({ ...current, action: event.target.value }))}>
                  <option value="shift_note">Shift note</option>
                  <option value="resource_exception">Resource exception</option>
                  <option value="cease_fire_note">Cease-fire note</option>
                  <option value="incident_note">Incident note</option>
                  <option value="guest_pass_note">Guest pass note</option>
                </select>
              </label>
              <label>
                Resource
                <select value={actionForm.resourceId} onChange={(event) => setActionForm((current) => ({ ...current, resourceId: event.target.value }))}>
                  <option value="">No resource</option>
                  {resources.map((resource) => (
                    <option key={resource.resourceId} value={resource.resourceId}>{resource.name}</option>
                  ))}
                </select>
              </label>
              <label>
                Related member (optional)
                <select value={actionForm.memberId} onChange={(event) => setActionForm((current) => ({ ...current, memberId: event.target.value }))}>
                  <option value="">No member</option>
                  {members.map((member) => (
                    <option key={member.memberId} value={member.memberId}>{memberLabel(member)}</option>
                  ))}
                </select>
              </label>
              <label>
                Note
                <textarea value={actionForm.note} onChange={(event) => setActionForm((current) => ({ ...current, note: event.target.value }))} rows={4} />
              </label>
              <button type="submit" disabled={savingOperationNote || !actionForm.note.trim()}>
                {savingOperationNote ? "Recording..." : "Record"}
              </button>
            </form>
          </TaskDisclosure>
          <div className="ops-log">
            {operationNotesLoading && <div className="empty-state">Loading shift notes...</div>}
            {!operationNotesLoading && visibleOpsLog.map((item) => (
              <div key={item.id}>
                <strong>{item.memberName}</strong>
                <span>{item.action.replace(/_/g, " ")} {item.resourceName ? `/ ${item.resourceName}` : ""}</span>
                <small>{formatDateTime(item.createdAt)} {item.durable ? "/ Saved" : "/ Current session"} {item.note ? `/ ${item.note}` : ""}</small>
              </div>
            ))}
            {!operationNotesLoading && visibleOpsLog.length === 0 && <div className="empty-state">No shift notes recorded yet.</div>}
          </div>
        </section>
      )}
    </div>
  );
}
